import { useEffect, useMemo, useRef, useState } from 'react';

import { LocationDTO, LocationSelectFormElementAnswerSchema, LocationSelectFormElement as LocationSelectFormElementType, RecordState } from '@gearbelt/global';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { AsyncSelect } from '@/components/ui/async-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MULTI_SELECT_STYLES } from '@/components/ui/multi-select';
import { DoubleClickInput } from '@/components/ui/select';
import { getAssetLocationQueryOptions, getLocationsQueryOptions } from '@/queries/locations';
import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useIsAWA } from '@/utils/awa';

import { ConfigLabel } from '../config-label';
import { FormFieldError } from '../form-field-error';

const formatLocationLabel = (location: LocationDTO) => location.name;

export const LocationSelectFormElement = ({
  element,
  value,
  onChange,
  onBlur,
  readonly,
  error,
  assetLocationId,
  onPrefill,
}: {
  element: LocationSelectFormElementType;
  value: z.infer<typeof LocationSelectFormElementAnswerSchema>['value'];
  onChange: (value: z.infer<typeof LocationSelectFormElementAnswerSchema>['value']) => void;
  onBlur?: () => void;
  readonly?: boolean;
  error?: string;
  assetLocationId?: string;
  /**
   * Seed the asset's location. Separate from `onChange` because a default is not
   * a user edit: routing it through `onChange` marks the form dirty and prompts
   * "Save changes?" on a record nobody touched.
   */
  onPrefill?: (value: string) => void;
}) => {
  const { isAWA } = useIsAWA();

  const ref = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lastSelectedLocation, setLastSelectedLocation] = useState<{ value: string; label: string } | null>(null);

  const { data: assetLocations } = useQuery(getAssetLocationQueryOptions({ assetLocationId, isAWA }));

  useEffect(() => {
    if (assetLocations?.[0]) {
      setLastSelectedLocation({
        value: assetLocations[0].id,
        label: formatLocationLabel(assetLocations[0]),
      });
    }
  }, [assetLocations]);

  const { data: locationsData } = useQuery(getLocationsQueryOptions({ limit: 100, isAWA, filters: { state: RecordState.Active } }));
  const locations = useMemo(() => locationsData?.data ?? [], [locationsData]);
  const options = useMemo(() => {
    return [
      ...locations.map((location) => ({
        value: location.id,
        label: formatLocationLabel(location),
      })),
      ...(lastSelectedLocation && !locations.some((location) => location.id === lastSelectedLocation.value)
        ? [{ value: lastSelectedLocation.value, label: lastSelectedLocation.label }]
        : []),
    ];
  }, [locations, lastSelectedLocation]);

  const populateLastLocation = element.config?.populateLastLocation ?? true;

  // The locations load async, so this lands after mount and would otherwise look
  // like an unprovoked edit.
  useEffect(() => {
    if (populateLastLocation && !value && assetLocationId && options.some((option) => option.value === assetLocationId)) {
      onPrefill?.(assetLocationId);
    }
  }, [populateLastLocation, value, assetLocationId, options, onPrefill]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const className = target?.getAttribute('class') || '';
      const clickedInsideRef = className.includes(element.id);
      if (clickedInsideRef) {
        return;
      }

      setIsMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [element.id]);

  return (
    <div className="space-y-1">
      <div ref={ref}>
        <Label required={element.required}>{element.label}</Label>
        <AsyncSelect<{ value: string; label: string }>
          cacheOptions
          defaultOptions={options}
          value={options.find((opt) => opt.value === value) || null}
          loadOptions={async (input: string) => {
            const { data } = await (
              await (isAWA ? AWAApiV2Service : ApiService).GetLocations({
                limit: 100,
                filters: {
                  state: RecordState.Active,
                  search: input,
                },
              })
            ).json();
            if (!data) return [];

            return data.map((location) => ({
              value: location.id,
              label: formatLocationLabel(location),
            }));
          }}
          onChange={(newValue) => {
            if (newValue?.value) {
              setLastSelectedLocation({ value: newValue?.value, label: newValue?.label });
            }
            onChange(newValue?.value || null);
          }}
          onBlur={onBlur}
          menuIsOpen={isMenuOpen}
          components={{
            Input: (props) => <DoubleClickInput {...props} setIsMenuOpen={setIsMenuOpen} />,
          }}
          styles={{
            control: (state) => ({
              ...state,
              borderColor: isMenuOpen ? 'rgb(var(--default-blue))' : 'rgb(var(--grey-3))',
              opacity: '1',
              backgroundColor: readonly ? 'rgb(var(--grey-1))' : state.backgroundColor,
            }),
          }}
          closeMenuOnScroll={(event) => {
            if (!(event.target instanceof Element)) return true;
            const target = event.target;
            const className = target.getAttribute('class') || '';

            if (className.includes(element.id) && (className.includes('__input') || className.includes('__menu'))) {
              setIsMenuOpen(true);
              return false;
            } else {
              setIsMenuOpen(false);
              return true;
            }
          }}
          blurInputOnSelect
          classNamePrefix={element.id}
          placeholder={`Select ${element.label}`}
          isClearable
          isDisabled={readonly}
          // @ts-expect-error - React Select has PITA types for this
          classNames={MULTI_SELECT_STYLES(!!error)}
        />
      </div>

      <FormFieldError error={error} />
    </div>
  );
};

export function LocationSelectFormElementMock({ element }: { element: LocationSelectFormElementType }) {
  const { isAWA } = useIsAWA();

  const ref = useRef<HTMLDivElement>(null);

  const [value, setValue] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { data: locationsData } = useQuery(getLocationsQueryOptions({ limit: 100, isAWA, filters: { state: RecordState.Active } }));
  const locations = useMemo(() => locationsData?.data ?? [], [locationsData]);
  const options = useMemo(
    () =>
      locations.map((location) => ({
        value: location.id,
        label: formatLocationLabel(location),
      })),
    [locations],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const className = target?.getAttribute('class') || '';
      const clickedInsideRef = className.includes(element.id);
      if (clickedInsideRef) {
        return;
      }

      setIsMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [element.id]);

  return (
    <div ref={ref}>
      <AsyncSelect<{ value: string; label: string }>
        cacheOptions
        defaultOptions={options}
        value={options.find((opt) => opt.value === value) || null}
        loadOptions={async (input: string) => {
          const { data } = await (
            await (isAWA ? AWAApiV2Service : ApiService).GetLocations({
              limit: 100,
              filters: {
                state: RecordState.Active,
                search: input,
              },
            })
          ).json();
          if (!data) return [];

          return data.map((location) => ({
            value: location.id,
            label: formatLocationLabel(location),
          }));
        }}
        onChange={(newValue) => {
          setValue(newValue?.value || null);
        }}
        menuIsOpen={isMenuOpen}
        components={{
          Input: (props) => <DoubleClickInput {...props} setIsMenuOpen={setIsMenuOpen} />,
        }}
        styles={{
          control: (state) => ({
            ...state,
            borderColor: isMenuOpen ? 'rgb(var(--default-blue))' : 'rgb(var(--grey-3))',
          }),
        }}
        blurInputOnSelect
        // DoubleClickInput opens the menu on focus; React Select then treats Tab as select-and-preventDefault
        // while the menu is open, trapping keyboard focus on the card. Let Tab bubble so it advances instead.
        tabSelectsValue={false}
        classNamePrefix={element.id}
        placeholder={`Select ${element.label}`}
        isClearable
        // @ts-expect-error - React Select has PITA types for this
        classNames={MULTI_SELECT_STYLES()}
      />
    </div>
  );
}

export function LocationSelectFormElementConfig({
  element,
  onChange,
}: {
  element: LocationSelectFormElementType;
  onChange?: (updates: Partial<LocationSelectFormElementType>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>
      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>
      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox
          checked={element.config?.populateLastLocation ?? true}
          onCheckedChange={(checked) => onChange?.({ config: { populateLastLocation: !!checked } })}
        />
        <span>Populate last location</span>
      </ConfigLabel>
    </div>
  );
}
